<template>
  <div class="p-6">
    <div class="bg-white rounded-lg shadow-md">
      <div class="px-4 py-5 sm:p-6">
        <div v-if="loading" class="flex justify-center items-center py-10">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
        <div v-else>
          <div class="flex justify-between items-center mb-6">
            <h1 class="text-xl font-bold">Партия поставщика {{ batchManufacture.number }}</h1>
          </div>

          <div class="mb-6 grid grid-cols-2 gap-6">
            <div class="bg-gray-50 p-4 rounded-md">
              <p class="text-sm font-medium text-gray-500">Дата изготовления</p>
              <p class="mt-1 font-medium text-gray-900">{{ formatDate(batchManufacture.manufacture_date) }}</p>
            </div>
            <div class="bg-gray-50 p-4 rounded-md">
              <p class="text-sm font-medium text-gray-500">Поставщик</p>
              <p class="mt-1 font-medium text-gray-900">{{ batchManufacture.supplier?.name || 'Не указан' }}</p>
            </div>
            <div class="bg-gray-50 p-4 rounded-md">
              <p class="text-sm font-medium text-gray-500">Срок годности</p>
              <p class="mt-1 font-medium text-gray-900">{{ batchManufacture.expiry_date ? formatDate(batchManufacture.expiry_date) : 'Не указан' }}</p>
            </div>
            <div class="bg-gray-50 p-4 rounded-md">
              <p class="text-sm font-medium text-gray-500">Всего продукции</p>
              <p class="mt-1 font-medium text-gray-900">{{ batchManufacture.products?.length || 0 }}</p>
            </div>
          </div>

          <div>
            <div class="flex justify-between items-center mb-4">
              <h2 class="text-lg font-medium text-gray-900">Продукция в партии</h2>
            </div>

            <div class="overflow-x-auto rounded-lg border border-gray-200">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Наименование</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Серийные номера и расположение</th>
                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Остаток</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-if="batchManufacture.products.length === 0">
                    <td colspan="3" class="px-6 py-4 text-center text-gray-500">
                      Нет продукции в партии
                    </td>
                  </tr>
                  <tr
                    v-for="product in batchManufacture.products"
                    :key="product.product_id"
                    class="hover:bg-gray-50"
                  >
                    <td class="px-6 py-4 align-top">
                      <div>
                        <span :class="{ 'line-through text-gray-500': product.product_deleted_at }" class="font-medium">{{ product.product_name }}</span>
                        <span v-if="product.product_deleted_at" class="ml-1 text-xs text-red-600 font-medium">Удалена</span>
                      </div>
                    </td>
                    <td class="px-6 py-4">
                      <div class="space-y-2">
                        <div
                          v-for="(serial, index) in product.serial_numbers"
                          :key="index"
                          class="text-sm"
                        >
                          <div class="flex items-start space-x-2">
                            <span class="font-mono text-blue-600 min-w-0 flex-shrink-0">
                              {{ serial.serial_number || 'Не указан' }}
                            </span>
                            <span class="text-gray-600">→</span>
                            <span class="text-gray-700 break-words">{{ serial.location }}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="px-6 py-4 whitespace-nowrap align-top">
                      <span class="font-medium text-gray-900">{{ formatQuantity(product.quantity) }} {{ getUnitLabel(product) }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-gray-50 px-4 py-4 sm:px-6 flex justify-end">
        <button
          @click="goBack"
          type="button"
          class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Назад к списку партий
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    moduleId: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      loading: true,
      batchManufacture: {
        id: null,
        number: '',
        supplier: null,
        manufacture_date: '',
        expiry_date: '',
        products: []
      }
    };
  },
  created() {
    if (this.$route.params.id) {
      this.fetchBatchManufacture(this.$route.params.id);
    }
  },
  mounted() {
    // Сбрасываем скролл в начало страницы при открытии просмотра партии
    window.scrollTo(0, 0);
  },
  methods: {
    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    getUnitLabel(product) {
      return product?.unit_short_name || product?.unit?.short_name || product?.product?.unit_short_name || 'шт';
    },
    async fetchBatchManufacture(id) {
      this.loading = true;

      try {
        const { data } = await axios.post('/api/batchManufacture/getBatchWithProducts', { id });

        if (data.success) {
          Object.assign(this.batchManufacture, data.data);
        } else {
          console.error('Ошибка при получении данных партии:', data);
        }
      } catch (error) {
        console.error('Ошибка при получении данных партии:', error);
      } finally {
        this.loading = false;
      }
    },

    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('ru-RU');
    },

    goBack() {
      const from = this.$route.query.from;
      const moduleId = this.moduleId || this.$route.query.moduleId || this.$route.params.moduleId;
      const returnTab = this.$route.query.returnTab;
      // Возврат на страницу склада с правильным табом
      const query = {tab: 'batches'};
        if (returnTab) {
          query.tab = returnTab;
        }
      this.$router.push({
        name: 'StorehouseIndex',
        params: { id: moduleId },
        query: query
      });
    }
  }
};
</script> 